<template>
  <div class="contact-page bg-base-200 min-h-screen py-4 md:py-8">
    <!-- Toast Notification -->
    <div class="toast toast-top toast-center">
      <div
        v-if="toast.show"
        :class="`alert ${toast.type === 'success' ? 'alert-success' : 'alert-error'}`"
      >
        <span>{{ toast.message }}</span>
      </div>
    </div>
    <div class="container mx-auto">
      <div class="card bg-base-100 shadow-xl p-4 md:p-8">
        <div class="text-left mb-8">
          <h1 class="text-5xl font-bold text-base-content">Get in Touch</h1>
          <p class="py-6 text-lg text-base-content opacity-80">
            We'd love to hear from you! Send us a message for general inquiries
            or connect with us on social media.
          </p>
        </div>

        <div class="flex flex-col lg:flex-row gap-8">
          <div class="flex-1">
            <form @submit.prevent="sendEmail" class="space-y-4">
              <h2 class="text-2xl font-bold mb-4">Send a Message</h2>
              <div class="form-control">
                <label class="label"
                  ><span class="label-text">Name</span></label
                >
                <input
                  v-model="formData.name"
                  type="text"
                  placeholder="Your Name"
                  class="input input-bordered"
                  required
                />
              </div>
              <div class="form-control">
                <label class="label"
                  ><span class="label-text">Email</span></label
                >
                <input
                  v-model="formData.email"
                  type="email"
                  placeholder="Your Email"
                  class="input input-bordered"
                  required
                />
              </div>
              <div class="form-control">
                <label class="label"
                  ><span class="label-text">Subject</span></label
                >
                <input
                  v-model="formData.subject"
                  type="text"
                  placeholder="Subject"
                  class="input input-bordered"
                  required
                />
              </div>
              <div class="form-control">
                <label class="label"
                  ><span class="label-text">Message</span></label
                >
                <textarea
                  v-model="formData.message"
                  class="textarea textarea-bordered h-32"
                  placeholder="Your message..."
                  required
                ></textarea>
              </div>
              <div class="form-control mt-6">
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="isLoading"
                >
                  <span v-if="isLoading" class="loading loading-spinner"></span>
                  Send Email
                </button>
              </div>
            </form>
          </div>

          <div class="flex-1 lg:max-w-sm">
            <div class="card bg-base-200 shadow-lg p-6">
              <h2 class="text-2xl font-bold mb-4">Contact Info</h2>
              <div class="space-y-4">
                <div>
                  <h3 class="font-bold text-lg mb-2">Booking</h3>
                  <div
                    v-for="contact in bookingContacts"
                    :key="contact.email"
                    class="mb-2"
                  >
                    <p class="font-semibold">{{ contact.name }}</p>
                    <a
                      :href="`mailto:${contact.email}`"
                      class="link link-primary"
                      >{{ contact.email }}</a
                    >
                  </div>
                </div>
                <div class="divider"></div>
                <div>
                  <h3 class="font-bold text-lg mb-2">Management</h3>
                  <p class="font-semibold">{{ managementContact.name }}</p>
                  <a
                    :href="`mailto:${managementContact.email}`"
                    class="link link-primary"
                    >{{ managementContact.email }}</a
                  >
                </div>
                <div class="divider"></div>
                <div>
                  <h3 class="font-bold text-lg mb-2">Follow Us</h3>
                  <div class="flex space-x-4">
                    <a
                      v-for="social in socials"
                      :key="social.name"
                      :href="social.url"
                      target="_blank"
                      rel="noopener"
                      class="btn btn-ghost btn-circle"
                    >
                      <i :class="`fab ${social.icon} text-2xl`"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useHead } from '@vueuse/head'
import emailjs from '@emailjs/browser'

const SERVICE_ID = 'service_dmaylf5'
const TEMPLATE_ID = 'template_2b3mi7k'
const USER_ID = 'user_6cTcGqYYyin2K27Pv3YnB'
const bookingContacts = [
  { name: 'David Limentani', email: 'David@minttalentgroup.com' },
]
const managementContact = { name: 'Oz McGuire', email: 'ozmcguire@gmail.com' }

useHead({
  title: 'Contact Us - Sqwerv',
  meta: [
    {
      hid: 'sqwervContactUsPage',
      name: 'description',
      content:
        'Contact Sqwerv for gigs, promotion, or any other inquiries. Reach out via our contact form or find us on social media.',
    },
  ],
})

const formData = ref({
  name: '',
  email: '',
  subject: '',
  message: '',
})
const isLoading = ref(false)
const toast = ref({
  show: false,
  message: '',
  type: 'success',
})

const showToast = (message, type = 'success') => {
  toast.value = { show: true, message, type }
  setTimeout(() => {
    toast.value.show = false
  }, 3000)
}

const socials = ref([
  {
    name: 'Spotify',
    url: 'https://open.spotify.com/artist/3xrbKno8shx3H22Xz8omC8?si=-ErxmcGVSQiUQrkaQ_ttbg',
    icon: 'fa-spotify',
  },
  {
    name: 'Apple Music',
    url: 'https://music.apple.com/us/artist/sqwerv/1564277335',
    icon: 'fa-apple',
  },
  {
    name: 'YouTube',
    url: 'https://www.youtube.com/channel/UCtbI8FWG4a1As9wx1E4pYyQ',
    icon: 'fa-youtube',
  },
  {
    name: 'Bandcamp',
    url: 'https://sqwerv.bandcamp.com/',
    icon: 'fa-bandcamp',
  },
])

const sendEmail = () => {
  isLoading.value = true

  emailjs
    .send(SERVICE_ID, TEMPLATE_ID, formData.value, USER_ID)
    .then(() => {
      showToast('Email sent successfully!', 'success')
      formData.value = { name: '', email: '', subject: '', message: '' }
    })
    .catch((error) => {
      console.error('EmailJS error:', error)
      showToast('Failed to send email. Please try again later.', 'error')
    })
    .finally(() => {
      isLoading.value = false
    })
}
</script>
